/**
 * Created by jonas on 2021-02-02.
 */
import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {PossessedGameData, PossessedInitData, WinData, WinLineData} from "./PossessedData";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {PossessedSymbolStates} from "../reels/PossessedSymbol";
import { Possessed } from '../Possessed';
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {PossessedGameMode} from "./PossessedGameMode";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {PossessedStatics} from "../config/PossessedStatics";
import {SlotKeypad} from "@nolimitcity/slot-keypad/bin/SlotKeypad";

export interface reelPosition {
    reelID:number,
    reelPosition:number
}
type reelPositionWithSymbol = reelPosition & {symbol:string};

export class PossessedModel extends GameModel<PossessedInitData, PossessedGameData> {
    public wins:WinData[];
    public parsedReels:reelPositionWithSymbol[];
    public parsedEvaluatedArea:reelPositionWithSymbol[];
    public buyFeatureName:string;
    public isRestoreFeatureBet:boolean;

    public isRestore:boolean = false;

    // https://github.com/nolimitcity/possessed/issues/218
    public isActionSpinsBet:boolean = false;

    constructor(api:ApiPlugin, triggerEvents:boolean = true) {
        super(api, triggerEvents);

        api.events.once("featureBet", (data:boolean) => {
            this.isRestoreFeatureBet = data;
        });
        api.events.once("featureName", (data:string) => {
            this.buyFeatureName = data;
        });
        Possessed.apiPlugin.events.on(APIEvent.BET, (betData:APIFeatureBetEventData) => {
            for (let index = 0; index < Possessed.model.initData.featureBuyTimesBetValue.length; index++) {
                if (Possessed.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    Logger.logDev("Opted Feature : ", this.buyFeatureName);
                    break;
                }
            }
        });

        Possessed.apiPlugin.events.on(APIEvent.ACTION_SPINS_BET, (betData:APIFeatureBetEventData) => {
            for (let index = 0; index < Possessed.model.initData.featureBuyTimesBetValue.length; index++) {
                if (Possessed.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    Logger.logDev("Opted Feature : ", this.buyFeatureName);
                    break;
                }
            }
            // https://github.com/nolimitcity/possessed/issues/218
            this.isActionSpinsBet = true;
        });
    }


    protected onInit(data:PossessedInitData):void {
        super.onInit(data);
        this.isRestore = data.isRestoreState;
    }

    protected onGame(data:PossessedGameData):void {
        super.onGame(data);
        this.isRestore = false;
        this.setupReels();
        this.setupEvaluatedArea();
        this.createWinData();
    }

    protected setupReels(): void {
        this.parsedReels =
            this.data.reels
                .map((reel, reelID) =>
                    reel.map((sym, reelPosition) => {
                        return {
                            symbol: sym,
                            reelID: reelID,
                            reelPosition: reelPosition - 1, //for overflow
                        }
                    }))
                .reduce((a,b) => a.concat(b), []);
    }
    
    // Returns parsed reel area with no overflow symbols
    protected getParsedVisibleReels():reelPositionWithSymbol[] {
        return this.parsedReels.filter(pos => pos.reelPosition >= 0 && pos.reelPosition < PossessedStatics.NUMBER_OF_ROWS[pos.reelID]);
    }

    protected setupEvaluatedArea(): void {
        this.parsedEvaluatedArea =
            this.gameData.evaluatedArea
                .map((reel, reelID) =>
                    reel.map((sym, reelPosition) => {
                        return {
                            symbol: sym,
                            reelID: reelID,
                            reelPosition: reelPosition
                        }
                    }))
                .reduce((a,b) => a.concat(b), []);
    }


    private createWinData() {
        this.wins =  this.gameData.betWayWins.map((win) => {
            return {
                winLineData: win,
                winningPositions: this.parsedEvaluatedArea.filter((pos) => pos.reelID < win.length &&
                                                                           (pos.symbol == win.symbol ||
                                                                            pos.symbol == "W"))
            }
        });
    }

    public getBetPrice(): number {
        const betLevel: string = Possessed.apiPlugin.freeBets.hasFreeBets() ? Possessed.apiPlugin.freeBets.getBet() : Possessed.apiPlugin.betLevel.getLevel();
        let price: number = this.gameData?.playedBetValue || parseFloat(betLevel);
        if (Possessed.model.data.wasFeatureBuy || Possessed.model.data.enhancedBet) {
            for (let index = 0; index < this.initData.featureBuyTimesBetValue.length; index++) {
                if (this.initData.featureBuyTimesBetValue[index].name === this.buyFeatureName) {
                    price *= this.initData.featureBuyTimesBetValue[index].price;
                    break;
                }
            }
        }
        return price;
    }

    // win stuff
    public getWonWays():number {
        let totalWays = 0;
        this.wins.forEach(win => {
            totalWays += win.winLineData.numberOfCombinations;
        })
        return totalWays;
    }

    public getThisSpinWinRatio():number {
        return Possessed.model.data.thisSpinWinning / Possessed.model.data.playedBetValue;
    }

    public previousSpinTotalWinnings():number {
        return Possessed.model.data.accumulatedRoundWin - Possessed.model.data.thisSpinWinning;
    }

    public getWinningPositions(): reelPosition[] {
        const positions = this.wins.map((win) => win.winningPositions).reduce((a,b) => a.concat(b), []);
        return Array.from(new Set([...positions]));
    }

    public getNonWinningPositions(): reelPosition[] {
        return this.parsedEvaluatedArea.filter((pos) => this.getWinningPositions().indexOf(pos) == -1);
    }

    public isWinBelowStake(): boolean {
        return Possessed.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && (this.data.accumulatedRoundWin <= this.getBetPrice());
    }

    // infectious xways stuff
    public getInfectiousXWaysSymbols(): (reelPosition & {targetSize: number, infectSize:number, newSymbol:string})[] {
        return this.getParsedVisibleReels()
            .filter((pos) => pos.symbol == "XIN")
            .map((pos) => {
                const target = this.parsedEvaluatedArea.find((pos2) => pos.reelPosition == pos2.reelPosition &&
                                                                       pos.reelID == pos2.reelID)
                return Object.assign({newSymbol: target!.symbol,
                                        targetSize: this.data.symbolSizes[pos.reelID][pos.reelPosition],
                                        infectSize: Math.max(this.data.symbolSizes[pos.reelID][pos.reelPosition] - this.data.symbolSizesXWays[pos.reelID][pos.reelPosition], 2)
                                    },
                                     pos);
            })
    }

    public getInfectiousXWaysTargets(): (reelPosition & {targetSize:number})[] {
        const infectiousSymbols = this.getInfectiousXWaysSymbols().map((pos) => pos.newSymbol);
        return this.parsedEvaluatedArea
            .filter((pos) => infectiousSymbols.some((sym) => sym == pos.symbol &&
                                                             pos.reelPosition > -1 &&
                                                             ((pos.reelID == 0 || pos.reelID == 4)
                                                               ? (pos.reelPosition < 3)
                                                               : (pos.reelPosition < 6))))
            .map((pos) => {
                return Object.assign({targetSize: this.data.symbolSizes[pos.reelID][pos.reelPosition]}, pos)
            })
    }

    // xways stuff
    public getXWaysSymbols(): (reelPosition & {newSymbol:string, targetSize:number})[] {
        return this.getParsedVisibleReels()
            .filter((pos) => pos.symbol == "XW")
            .map((pos) => {
                const target = this.parsedEvaluatedArea.find((pos2) => pos.reelPosition == pos2.reelPosition &&
                                                                       pos.reelID == pos2.reelID)
                const size = this.data.symbolSizesXWays[pos.reelID][pos.reelPosition];
                return Object.assign({newSymbol: target!.symbol, targetSize: size}, pos);
            })
    }

    // wild cross / symbol stuff
    public getWildSymbol(): (reelPosition & {isFullCross:boolean, shouldExpand:boolean}) | undefined {
        const wildSymbol = this.parsedReels.find((pos) => pos.symbol == "W" &&
                                                          pos.reelPosition > -1 &&
                                                          ((pos.reelID == 0 || pos.reelID == 4)
                                                              ? (pos.reelPosition < 3)
                                                              : (pos.reelPosition < 6)));
        return wildSymbol == undefined
               ? undefined
               : Object.assign({isFullCross: wildSymbol.reelPosition == 3,
                                shouldExpand: (wildSymbol.reelPosition > 0 &&
                                              wildSymbol.reelPosition < 5)},
                               wildSymbol);
    }

    // other symbol stuff
    public getActivationSymbol(): reelPosition[] {
        return this.parsedEvaluatedArea.filter((pos) => pos.symbol == "XA")
    }

    // other symbol stuff
    public getScatterSymbols(): reelPosition[] {
        return this.parsedEvaluatedArea.filter((pos) => pos.symbol == "S")
    }

    // Hotzone and reelCross stuff

    public hasFullCrossSymbol():boolean {
        const crossPositions:reelPosition[] = [
            {reelID: 1, reelPosition: 3},
            {reelID: 2, reelPosition: 1},
            {reelID: 2, reelPosition: 2},
            {reelID: 2, reelPosition: 3},
            {reelID: 2, reelPosition: 4},
            {reelID: 3, reelPosition: 3},
        ];
        if(!this.gameData) return false;

        const crossSymbols = this.parsedEvaluatedArea.filter(sym => crossPositions.some(s => s.reelPosition == sym.reelPosition && s.reelID == sym.reelID));
        return (crossSymbols.filter(s => s.symbol == crossSymbols[0].symbol).length == 6) && this.gameData.hotZoneSize == 6;
    }

    public getFullCrossSymbolName():string|undefined {
        if(this.hasFullCrossSymbol()) {
            return this.parsedEvaluatedArea.find(s => s.reelID == 1 && s.reelPosition == 3)?.symbol;
        } else {
            return undefined;
        }
    }

    // Returns symbols revealed in hotzones that are not wild symbols.
    public getHotZoneSymbols():reelPositionWithSymbol[] {
        let symbols:reelPositionWithSymbol[] = [];
        const crossPositions:reelPosition[] = [
            {reelID: 1, reelPosition: 3},
            {reelID: 2, reelPosition: 4},
            {reelID: 3, reelPosition: 3},
            {reelID: 2, reelPosition: 3},
            {reelID: 2, reelPosition: 2},
            {reelID: 2, reelPosition: 1},
        ];
        const hotZonePositions = crossPositions.slice(0, this.data.hotZoneSize);
        hotZonePositions.forEach(pos => {
            const symbol:string|undefined = this.parsedEvaluatedArea.find(s => s.reelPosition == pos.reelPosition && s.reelID == pos.reelID)?.symbol;
            if(symbol && symbol != "W") {
                symbols.push({reelPosition: pos.reelPosition, reelID: pos.reelID, symbol});
            }
        });
        return symbols;
    }

    // game mode stuff, might not be useful but who knows

    public isBoostedBet():boolean {
        return Possessed.apiPlugin.betHandler.betBoost != undefined;
    }

    public isNormalSpin(): boolean {
        return this.data.mode == PossessedGameMode.NORMAL;
    }

    public isRespin(): boolean {
        return this.data.mode == PossessedGameMode.RESPIN;
    }
    
    public isLastRespin():boolean {
        return this.data.mode == PossessedGameMode.RESPIN && this.data.nextMode != PossessedGameMode.RESPIN;
    }

    public triggeredFreespins(): boolean {
        return (this.isRespin() || this.isNormalSpin()) &&
               !(this.data.nextMode == PossessedGameMode.RESPIN ||
                 this.data.nextMode == PossessedGameMode.NORMAL);
    }

    public triggeredRespin():boolean {
        return this.data.mode != "RESPIN" && this.data.nextMode == "RESPIN";
    }

    public isLastFreespin():boolean {
        return this.isFreespins() && this.data.nextMode == PossessedGameMode.NORMAL;
    }

    public isFreespins(): boolean {
        return this.data.mode == PossessedGameMode.FREESPIN || this.data.mode == PossessedGameMode.FREESPIN_SUPER;
    }
}